// Description of an echocardiography report (Firestore collection "echos"). Values are stored
// by section, as in the former page: { identite: {...}, mensuration: {...}, mitrale: {...}, ... }.
import { allows, rightsOf, unitsOf } from '../../lib/permissions';
import { yearsSince } from '../../lib/format';
import { birthDateOf } from '../patients/patientModel';

// Values kept exactly as stored by the former page (some end with a space).
export const CARDIOLOGISTS = ['Pr Bara DIOP ', 'Pr Momar DIOUM ', 'Dr El Hadj Mbacké SARR ', 'Dr Sidy Lamine SY ', 'Dr Arame DIAGNE ', 'Dr Magalie KAYA ', 'Dr Elisabeth TINE ', 'Dr SYLLA ', 'Dr Khadija GUEYE ', 'Dr Ismael HANIFA '];
const GRADES = ['0', '1', '2', '3', '4'];

const num = (name, label, unit, extra = {}) => ({ name, label, unit, inputType: 'number', ...extra });
const grade = (name, label) => ({ name, label, type: 'select', options: GRADES });

export const ECHO_SECTIONS = [
  {
    id: 'patient',
    group: 'identite',
    title: 'Patient et examen',
    fields: [
      { name: 'nom', label: 'Nom' },
      { name: 'prenom', label: 'Prénom' },
      { name: 'dateNaissance', label: 'Date de naissance', type: 'date' },
      num('age', 'Âge', 'ans'),
      { name: 'sexe', label: 'Sexe', type: 'segmented', options: [{ value: 'M', label: 'Masculin' }, { value: 'F', label: 'Féminin' }] },
      { name: 'telephone', label: 'Téléphone', inputType: 'tel' },
      { name: 'adresse', label: 'Adresse', wide: true },
      { name: 'serviceDemandeur', label: 'Service demandeur' },
      { name: 'diagnostic', label: 'Diagnostic' },
      { name: 'operateur', label: 'Opérateur(s)', type: 'multi', options: CARDIOLOGISTS, wide: true },
    ],
  },
  {
    id: 'constantes',
    group: 'identite',
    title: 'Constantes',
    fields: [
      num('poids', 'Poids', 'kg', { step: '0.1' }),
      num('taille', 'Taille', 'cm'),
      num('surfaceCorporelle', 'Surface corporelle', 'm²', { step: '0.01', hint: 'Calculée à partir du poids et de la taille.' }),
      { name: 'ta', label: 'Tension artérielle', unit: 'mmHg', placeholder: 'ex. 12/8' },
      num('FC', 'Fréquence cardiaque', 'bpm'),
      num('spo2', 'SpO₂', '%'),
      num('temperature', 'Température', '°C', { step: '0.1' }),
    ],
  },
  {
    id: 'mensuration',
    group: 'mensuration',
    title: 'Mensurations écho 2D-TM',
    fields: [
      num('dvgDiastolique', 'DVG diastolique', 'mm'),
      num('dvgSystolique', 'DVG systolique', 'mm'),
      num('septumDiastolique', 'Septum diastolique', 'mm'),
      num('septumSystolique', 'Septum systolique', 'mm'),
      num('paroiPostDiastolique', 'Paroi post. diastolique', 'mm'),
      num('pericarde', 'Péricarde', 'mm'),
      num('dog', 'DOG', 'mm'),
      num('dao', 'DAO', 'mm'),
      num('dvd', 'DVD', 'mm'),
      num('raccourcissement', 'Raccourcissement', '%'),
      num('fractionEjection', 'Fraction d’éjection', '%'),
      num('tapse', 'TAPSE', 'mm'),
      num('vciInsp', 'VCI inspiration', 'mm'),
      num('vciExp', 'VCI expiration', 'mm'),
      num('sog', 'SOG', 'mm'),
      num('sod', 'SOD', 'mm'),
    ],
  },
  {
    id: 'mitrale',
    group: 'mitrale',
    title: 'Doppler mitral',
    fields: [
      num('surfaceMitralePlanimetrie', 'Surface mitrale (planimétrie)', 'cm²', { step: '0.01' }),
      num('surfaceMitralePHT', 'Surface mitrale (PHT)', 'cm²', { step: '0.01' }),
      grade('imGrade', 'IM grade'),
      num('vc', 'VC', 'mm'),
      num('sor', 'SOR', 'mm²'),
      num('vr', 'VR', 'ml'),
      num('gradientMax', 'Gradient max', 'mmHg', { step: '0.1' }),
      num('gradientMoyen', 'Gradient moyen', 'mmHg', { step: '0.1' }),
      num('ondeE', 'Onde E', 'cm/s'),
      num('ondeA', 'Onde A', 'cm/s'),
      num('ea', 'E/A', '', { step: '0.01' }),
      num('ondeEa', 'Onde E’', 'cm/s'),
      num('ondeAa', 'Onde A’', 'cm/s'),
      num('eAa', 'E/A’', '', { step: '0.01' }),
      num('triv', 'TRIV', 'ms'),
      num('anneau', 'Anneau', 'mm'),
    ],
  },
  {
    id: 'aorte',
    group: 'aorte',
    title: 'Doppler aortique',
    fields: [
      grade('iaGrade', 'IA grade'),
      num('vc', 'VC', 'mm'),
      num('sor', 'SOR', 'mm²'),
      num('vr', 'VR', 'ml'),
      num('pht', 'PHT', 'ms'),
      { name: 'refluxCrosse', label: 'Reflux crosse' },
      num('gradientMax', 'Gradient max', 'mmHg', { step: '0.1' }),
      num('gradientMoyen', 'Gradient moyen', 'mmHg', { step: '0.1' }),
      num('vmax', 'Vmax', 'm/s', { step: '0.01' }),
      num('surfaceAortique', 'Surface aortique', 'cm²', { step: '0.01' }),
      num('valSalva', 'Valsalva', 'mm'),
      num('jst', 'JST', 'mm'),
      num('aorteAscendante', 'Aorte ascendante', 'mm'),
      num('aorteDescendante', 'Aorte descendante', 'mm'),
      num('aorteAbdominale', 'Aorte abdominale', 'mm'),
      num('anneau', 'Anneau', 'mm'),
    ],
  },
  {
    id: 'tricuspide',
    group: 'tricuspide',
    title: 'Doppler tricuspide',
    fields: [
      grade('itGrade', 'IT grade'),
      num('gradientVdOd', 'Gradient VD-OD', 'mmHg'),
      num('pod', 'POD', 'mmHg'),
      num('paps', 'PAPS', 'mmHg'),
      num('papd', 'PAPD', 'mmHg'),
      num('papMoy', 'PAP moyenne', 'mmHg'),
      num('anneau', 'Anneau', 'mm'),
      num('Colombo', 'Colombo', 'mm/m² SC', { step: '0.1' }),
    ],
  },
  {
    id: 'pulmonaire',
    group: 'pulmonaire',
    title: 'Doppler pulmonaire',
    fields: [
      grade('ipGrade', 'IP grade'),
      num('gradientMax', 'Gradient max', 'mmHg', { step: '0.1' }),
      num('gradientMoyen', 'Gradient moyen', 'mmHg', { step: '0.1' }),
      num('vmax', 'Vmax', 'm/s', { step: '0.01' }),
      num('anneau', 'Anneau', 'mm'),
      num('apd', 'APD', 'mm'),
      num('apg', 'APG', 'mm'),
      num('tronc', 'Tronc', 'mm'),
    ],
  },
  {
    id: 'conclusion',
    group: 'conclusion',
    title: 'Conclusion',
    fields: [
      { name: 'resume', label: 'Conclusion', type: 'textarea', wide: true },
      { name: 'commentaire', label: 'Commentaires', type: 'textarea', wide: true },
    ],
  },
];

export const GROUPS = ['identite', 'mensuration', 'mitrale', 'aorte', 'tricuspide', 'pulmonaire', 'conclusion'];

// The former form saved the heart rate as "FC" while its exports read "fc".
export const valueOf = (echo, group, name) => {
  const values = (echo && echo[group]) || {};
  if (group === 'identite' && name === 'FC') return values.FC || values.fc || '';
  const value = values[name];
  return value === undefined || value === null ? '' : value;
};

export const filled = (value) => (Array.isArray(value) ? value.filter(Boolean).length > 0 : String(value ?? '').trim() !== '');

export const patientName = (echo) => {
  const identite = (echo && echo.identite) || {};
  return [identite.prenom, identite.nom].map((part) => String(part || '').trim()).filter(Boolean).join(' ') || 'Patient non précisé';
};

export const examDate = (echo) => echo.dateExamen || echo.dateCreation;

export const operators = (echo) =>
  (Array.isArray(echo.identite && echo.identite.operateur) ? echo.identite.operateur : [echo.identite && echo.identite.operateur])
    .map((item) => String(item || '').trim())
    .filter(Boolean);

// Body surface area (Mosteller).
export const bodySurface = (weight, height) => {
  const kg = parseFloat(String(weight).replace(',', '.'));
  const cm = parseFloat(String(height).replace(',', '.'));
  return kg > 0 && cm > 0 ? Math.sqrt((kg * cm) / 3600).toFixed(2) : '';
};

// Identity fields filled from the patient file (only where the report is still empty).
export const fromPatient = (patient) => {
  const birth = birthDateOf(patient);
  const years = yearsSince(birth);
  return {
    nom: patient.nom || '',
    dateNaissance: birth ? String(birth).slice(0, 10) : '',
    age: years !== null ? String(years) : '',
    sexe: patient.genre === 'Feminin' ? 'F' : patient.genre === 'Masculin' ? 'M' : '',
    telephone: patient.numeroDeTelephone || '',
    adresse: patient.addressDomicile || '',
    poids: patient.poids || '',
    taille: patient.taille || '',
    ta: patient.ta || '',
    FC: patient.fc || '',
    spo2: patient.spo2 || '',
    temperature: patient.temperature || '',
    diagnostic: String(patient.diagnostic || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .join(', '),
  };
};

const normalize = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

// Patient files whose name matches the report (older reports were not linked).
export const likelyPatients = (echo, patients) => {
  const identite = (echo && echo.identite) || {};
  const names = [`${identite.prenom || ''} ${identite.nom || ''}`, `${identite.nom || ''} ${identite.prenom || ''}`, identite.nom]
    .map(normalize)
    .filter((name) => name.length > 2);
  if (!names.length) return [];
  return patients.filter((patient) => names.includes(normalize(patient.nom)));
};

export const searchText = (echo, linked) =>
  [
    patientName(echo),
    echo.identite && echo.identite.diagnostic,
    echo.identite && echo.identite.serviceDemandeur,
    operators(echo).join(' '),
    echo.conclusion && echo.conclusion.resume,
    linked && linked.nom,
    linked && linked.dossierNumber,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

// Reports are written by the cardiology unit (and the administration).
export const echoAccess = (user) => {
  const rights = rightsOf(user);
  const cardiology = user.role === 'Admin' || unitsOf(user.service).includes('cardiologie');
  return {
    canView: Boolean(rights.echo),
    canWrite: allows(rights, 'echo', 'modifier') && cardiology,
    canDelete: allows(rights, 'echo', 'supprimer') && cardiology,
    canExport: allows(rights, 'echo', 'tout'),
  };
};
